import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';

// Async thunks for API calls
export const fetchSubcategories = createAsyncThunk(
  'subcategories/fetchSubcategories',
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiService.getSubcategories();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addSubcategoryAsync = createAsyncThunk(
  'subcategories/addSubcategory',
  async (subcategory, { rejectWithValue }) => {
    try {
      const data = await apiService.addSubcategory(subcategory);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateSubcategoryAsync = createAsyncThunk(
  'subcategories/updateSubcategory',
  async ({ id, subcategory }, { rejectWithValue }) => {
    try {
      const data = await apiService.updateSubcategory(id, subcategory);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSubcategoryAsync = createAsyncThunk(
  'subcategories/deleteSubcategory',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.deleteSubcategory(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Load initial data from localStorage (for migration from old system)
const getInitialSubcategories = () => {
  try {
    const stored = localStorage.getItem('Subcategories');
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (e) {
    console.error('Error loading subcategories from localStorage:', e);
  }
  return [];
};

const subcategoriesSlice = createSlice({
  name: 'subcategories',
  initialState: {
    items: getInitialSubcategories(),
    loading: false,
    error: null,
  },
  reducers: {
    setSubcategories: (state, action) => {
      state.items = action.payload;
    },
    addSubcategory: (state, action) => {
      state.items.push(action.payload);
    },
    updateSubcategory: (state, action) => {
      const index = state.items.findIndex(
        sub => String(sub.id) === String(action.payload.id)
      );
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    deleteSubcategory: (state, action) => {
      state.items = state.items.filter(
        sub => String(sub.id) !== String(action.payload)
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch subcategories
      .addCase(fetchSubcategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubcategories.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchSubcategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add subcategory
      .addCase(addSubcategoryAsync.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      // Update subcategory
      .addCase(updateSubcategoryAsync.fulfilled, (state, action) => {
        const index = state.items.findIndex(
          sub => String(sub.id) === String(action.payload.id)
        );
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      // Delete subcategory
      .addCase(deleteSubcategoryAsync.fulfilled, (state, action) => {
        state.items = state.items.filter(
          sub => String(sub.id) !== String(action.payload)
        );
      });
  },
});

export const { setSubcategories, addSubcategory, updateSubcategory, deleteSubcategory } = subcategoriesSlice.actions;
export default subcategoriesSlice.reducer;

